import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { AdminApi, AdminGroupPermission } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons } from 'ui/molecules/modal';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';

interface DeletePermissionModalProps {
  onDelete: () => void;
  onClose: () => void;
  permission: AdminGroupPermission;
  groupName: string;
}

const DeletePermissionModal: FunctionComponent<DeletePermissionModalProps> = ({
  onDelete,
  onClose,
  permission,
  groupName,
}) => {
  const { makeAuthenticatedApi, withApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const translate = useTranslateWithStringArgs();

  const onSubmit = useCallback(() => {
    withApi(async () => {
      await adminApi.adminGroupPermissionsDestroy({ id: permission.id });

      onDelete();
    });
  }, [permission]);

  return (
    <Modal onClose={onClose}>
      <ModalHeader>
        <Translate name={'deletePermissionModal.title'} />
      </ModalHeader>
      <ModalContent>
        <Translate
          name="deletePermissionModal.content"
          args={[
            <strong>{translate(`permissionNames.${snakeToCamelCaseWithNumbers(permission.permissionCode)}`)}</strong>,
            <strong>{translate(`permissionTypes.${snakeToCamelCaseWithNumbers(permission.permissionType)}`)}</strong>,
            <strong>{groupName}</strong>,
          ]}
        />
      </ModalContent>
      <ModalFooter>
        <ModalFooterButtons
          actionButtons={[
            {
              name: 'cancel',
              content: <Translate name="common.cancel" />,
              size: 'large',
              onClick: () => onClose(),
            },
            {
              name: 'send',
              onClick: () => onSubmit(),
              content: <Translate name="common.remove" />,
              variant: 'primary',
              type: 'submit',
              size: 'large',
            },
          ]}
        />
      </ModalFooter>
    </Modal>
  );
};

export default DeletePermissionModal;
